'use client';

import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useRouter } from 'next/navigation';

export type UserRole = 'admin' | 'organizer' | 'user' | 'track_manager';

export interface AuthUser {
  id: string;
  email: string;
  full_name: string;
  race_name: string;
  role: UserRole;
  phone?: string;
}

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, fullName: string, phone: string, raceName: string, icNumber: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  isAdmin: boolean;
  isOrganizer: boolean;
  isTrackManager: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  name?: string;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// In-memory profile cache to avoid redundant DB calls on tab switch / navigation
const profileCache = new Map<string, { profile: AuthUser; ts: number }>();
const CACHE_TTL = 15 * 60 * 1000; // 15 minutes

// Single shared Supabase client — created once at module level
const supabase = createClient();

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const router = useRouter();

  const buildUser = useCallback(async (supabaseUser: any): Promise<AuthUser | null> => {
    if (!supabaseUser) return null;

    // Return cached profile to avoid slow DB call on every navigation / tab switch
    const cached = profileCache.get(supabaseUser.id);
    if (cached && Date.now() - cached.ts < CACHE_TTL) {
      return cached.profile;
    }

    try {
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('full_name, race_name, role, phone')
        .eq('id', supabaseUser.id)
        .maybeSingle();

      const role: UserRole =
        (profile?.role as UserRole) ||
        (supabaseUser.user_metadata?.role as UserRole) ||
        'user';

      const authUser: AuthUser = {
        id: supabaseUser.id,
        email: supabaseUser.email ?? '',
        full_name: profile?.full_name || supabaseUser.user_metadata?.full_name || '',
        race_name: profile?.race_name || supabaseUser.user_metadata?.race_name || '',
        role,
        phone: profile?.phone || '',
      };

      profileCache.set(supabaseUser.id, { profile: authUser, ts: Date.now() });
      return authUser;
    } catch {
      // On DB error: return a fallback from metadata — do NOT sign the user out
      const fallback: AuthUser = {
        id: supabaseUser.id,
        email: supabaseUser.email ?? '',
        full_name: supabaseUser.user_metadata?.full_name || '',
        race_name: supabaseUser.user_metadata?.race_name || '',
        role: (supabaseUser.user_metadata?.role as UserRole) || 'user',
      };
      return fallback;
    }
  }, []); // stable — supabase is module-level singleton

  const doSignOut = useCallback(async () => {
    await supabase.auth.signOut();
    setUser(null);
    profileCache.clear();
    router.push('/');
  }, [router]);

  useEffect(() => {
    // Subscribe to auth state changes ONCE.
    // onAuthStateChange fires INITIAL_SESSION immediately with the persisted
    // session from localStorage/cookies — no need for a separate getSession() call.
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_OUT') {
        setUser(null);
        setLoading(false);
        return;
      }

      if (session?.user) {
        // For TOKEN_REFRESHED: use cache to avoid a DB round-trip
        if (event === 'TOKEN_REFRESHED') {
          const cached = profileCache.get(session.user.id);
          if (cached) {
            setUser(cached.profile);
            setLoading(false);
            return;
          }
        }
        const u = await buildUser(session.user);
        setUser(u);
      } else if (event === 'INITIAL_SESSION') {
        // No session on startup — user is not logged in
        setUser(null);
      }

      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, [buildUser]); // buildUser is stable (no deps that change)

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
  };

  const signUp = async (email: string, password: string, fullName: string, phone: string, raceName: string = '', icNumber: string = '') => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { full_name: fullName, phone, role: 'user', race_name: raceName },
        emailRedirectTo: `${process.env.NEXT_PUBLIC_SITE_URL}/auth/callback`,
      },
    });
    if (error) throw error;
    if (data?.user) {
      await supabase.from('user_profiles').upsert({
        id: data.user.id,
        email,
        full_name: fullName,
        phone,
        race_name: raceName,
        ic_number: icNumber,
      });
    }
  };

  const signOut = async () => {
    await doSignOut();
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${process.env.NEXT_PUBLIC_SITE_URL}/auth/callback?next=/reset-password`,
    });
    if (error) throw error;
  };

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      await signIn(email, password);
      return true;
    } catch {
      return false;
    }
  };
  const logout = () => { signOut(); };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        signIn,
        signUp,
        signOut,
        resetPassword,
        isAdmin: user?.role === 'admin',
        isOrganizer: user?.role === 'organizer',
        isTrackManager: user?.role === 'track_manager',
        login,
        logout,
        name: user?.race_name || user?.full_name,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}